/**
 * Country sidebar component
 */

import React, { useState } from 'react';
import { useAppContext } from '../../context/AppContext';
import CountryHeader from './country-sidebar/CountryHeader';
import ManagerDistribution from './country-sidebar/ManagerDistribution';
import ClientList from './country-sidebar/ClientList';
import ClientTooltip from './country-sidebar/ClientTooltip';
import '../../styles/components/sidebar/CountrySidebar.css';

/**
 * Sidebar displaying country and client information
 * @param {Object} props - Component props
 * @param {Object} props.country - Country data to display
 * @param {Function} props.onClose - Callback when sidebar is closed
 * @returns {JSX.Element} CountrySidebar component
 */
const CountrySidebar = ({ country, onClose }) => {
  // State declarations at top level
  const [hoveredClient, setHoveredClient] = useState(null);
  
  // Get app context data
  const { 
    clientData,
    filteredClientData,
    isPinned,
    togglePinClient,
    pinCountryClients,
    unpinCountryClients
  } = useAppContext();
  
  // Early return if no country
  if (!country) return null;
  
  // Filter clients for this country - use filteredClientData for year filtering
  const countryClients = filteredClientData.filter(client => 
    client.country === country.country
  );

  // Calculate pin states for this country
  const pinnedCount = countryClients.filter(client => isPinned(client)).length;
  const unpinnedCount = countryClients.filter(client => !isPinned(client)).length;
  const allPinned = pinnedCount === countryClients.length && countryClients.length > 0;
  const nonePinned = pinnedCount === 0;
  const somePinned = pinnedCount > 0 && pinnedCount < countryClients.length;
  
  // Handle toggle pin all/unpin all for country
  const handleTogglePinAllCountry = () => {
    if (allPinned) {
      // All are pinned, so unpin all
      const removedCount = unpinCountryClients(country.country);
      console.log(`Unpinned ${removedCount} clients from ${country.country}`);
    } else {
      // Some or none are pinned, so pin all
      const addedCount = pinCountryClients(country.country, clientData);
      console.log(`Pinned ${addedCount} clients from ${country.country}`);
    }
  };
  
  // Get pin header text and style based on state
  const getPinHeaderState = () => {
    if (countryClients.length === 0) {
      return { text: 'Pin', clickable: false, title: 'No clients in this country' };
    } else if (allPinned) {
      return { text: 'Pin ✓', clickable: true, title: `Unpin all ${countryClients.length} clients from ${country.country}` };
    } else if (nonePinned) {
      return { text: 'Pin', clickable: true, title: `Pin all ${countryClients.length} clients from ${country.country}` };
    } else {
      return { text: `Pin (${pinnedCount}/${countryClients.length})`, clickable: true, title: `Pin remaining ${unpinnedCount} clients from ${country.country}` };
    }
  };
  
  const pinHeaderState = getPinHeaderState();
  
  return (
    <div className="country-sidebar">
      <CountryHeader 
        country={country} 
        clientData={filteredClientData} 
        onClose={onClose} 
      />
      
      <div className="sidebar-content">
        <ManagerDistribution 
          country={country} 
          clientData={filteredClientData} 
        />
        
        <ClientList 
          country={country} 
          clientData={filteredClientData} 
          isPinned={isPinned}
          togglePinClient={togglePinClient}
          setHoveredClient={setHoveredClient}
          pinHeaderState={pinHeaderState}
          handleTogglePinAllCountry={handleTogglePinAllCountry}
        />
      </div>
      
      <ClientTooltip hoveredClient={hoveredClient} />
    </div>
  );
};

export default CountrySidebar;
